import download from 'downloadjs'
import { combineEpics, ofType } from 'redux-observable'
import { of } from 'rxjs'
import { catchError, filter, map, mergeMap, takeUntil } from 'rxjs/operators'

import { wrapCSVTextToBlob, xhrExtractFileName } from 'utils/helpers'

import { addToast } from 'containers/Toasts/actions'

import { downloadOriginalEmailError, downloadOriginalEmailSuccess } from '../Report/actions'

import {
  clusterUndoError,
  clusterUndoSuccess,
  downloadCsvError,
  downloadCsvSuccess,
  downloadEmailError,
  fetchDataError,
  fetchDataSuccess,
  revertBackToOriginError,
  revertBackToOriginSuccess,
  stopRemediationError,
  stopRemediationSuccess,
} from './actions'
import {
  ATTACHMENTS_AND_LINKS_FILTER_CHANGE,
  CLUSTER_UNDO_ASYNC,
  DATA_REQUEST_ASYNC,
  DOWNLOAD_CSV_REQUEST,
  DOWNLOAD_EMAIL_ASYNC,
  END_USERS_FILTER_CHANGE,
  getSuccessToastMessage,
  IPS_FILTER_CHANGE,
  IS_READ_FILTER_CHANGE,
  JUNK_MAIL_FILTER_CHANGE,
  MAILBOX_FILTER_CHANGE,
  NOT_FOUND_FILTER_CHANGE,
  PAGINATE_CHANGE,
  REVERT_BACK_TO_ORIGIN_ASYNC,
  SEARCH_CHANGE,
  SENDER_FILTER_CHANGE,
  SORT_PROPERTY_CHANGE,
  STOP_REMEDIATION_ASYNC,
  SUBJECT_FILTER_CHANGE,
  TOGGLE_RELEASE_FILTER,
} from './constants'

const getBodyForReclassify = (state) => ({
  reclusterAll: state.selectAll,
  senderNameFilter: state.selectAll ? state.senderFilter.map((elem) => elem.value) : '',
  emailSubjectFilter: state.selectAll ? state.subjectFilter.map((elem) => elem.value) : '',
  recipientAddressFilter: state.selectAll ? state.mailboxFilter.map((elem) => elem.value) : '',
  ipAddressFilter: state.selectAll ? state.ipsFilter.map((elem) => elem.value) : '',
  attachmentsAndLinksFilter: state.selectAll ? state.attachmentsAndLinksFilter : '',
  notFoundFilter: state.selectAll ? state.notFoundFilter : '',
  isReadFilter: state.selectAll ? state.isReadFilter : '',
  search: state.search,
  includeEmails: !state.selectAll ? state.includeEmails : [],
  excludeEmails: state.selectAll ? state.excludeEmails : [],
  firstMitigationId: parseInt(Object.keys(state.data.results)[0]),
})

const fetchDataEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(
      DATA_REQUEST_ASYNC.INITIATE,
      SORT_PROPERTY_CHANGE,
      SEARCH_CHANGE,
      PAGINATE_CHANGE,
      SENDER_FILTER_CHANGE,
      SUBJECT_FILTER_CHANGE,
      MAILBOX_FILTER_CHANGE,
      IPS_FILTER_CHANGE,
      JUNK_MAIL_FILTER_CHANGE,
      NOT_FOUND_FILTER_CHANGE,
      ATTACHMENTS_AND_LINKS_FILTER_CHANGE,
      IS_READ_FILTER_CHANGE,
      CLUSTER_UNDO_ASYNC.SUCCESS,
      STOP_REMEDIATION_ASYNC.SUCCESS,
      REVERT_BACK_TO_ORIGIN_ASYNC.SUCCESS,
      TOGGLE_RELEASE_FILTER,
      END_USERS_FILTER_CHANGE
    ),
    filter(() => state$.value.resolutioncenter.affMailboxesDetail.reportId),
    mergeMap(() => {
      const state = state$.value.resolutioncenter.affMailboxesDetail
      return api.resolutionCenter
        .getAffectedMailboxesDetail(
          state.reportId,
          {
            page: state.page || 1,
            search: state.search,
            sortProperty: state.sortProperty,
            senderNameFilter: state.senderFilter.map((elem) => elem.value),
            emailSubjectFilter: state.subjectFilter.map((elem) => elem.value),
            recipientAddressFilter: state.mailboxFilter.map((elem) => elem.value),
            ipAddressFilter: state.ipsFilter.map((elem) => elem.value),
            junkMailFilter: state.junkMailFilter.map((elem) => elem.value),
            attachmentsAndLinksFilter: state.attachmentsAndLinksFilter,
            notFoundFilter: state.notFoundFilter,
            isReadFilter: state.isReadFilter,
            includeCheckboxValues: true,
            reviewRequestedFilter: state.releaseRequestFilter,
            filterByEndUser: state.endUsersFilter,
          },
          false,
          state$.value.partnerOptions.selectedCompany?.value
        )
        .pipe(
          map((response) => fetchDataSuccess(response.response)),
          catchError((error) => of(epicsErrorHandler(fetchDataError, error))),
          takeUntil(action$.pipe(ofType(DATA_REQUEST_ASYNC.CANCEL)))
        )
    })
  )

const downloadCsvEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(DOWNLOAD_CSV_REQUEST.INITIATE),
    mergeMap(() => {
      const state = state$.value.resolutioncenter.affMailboxesDetail
      return api.resolutionCenter
        .getAffectedMailboxesDetail(
          state.reportId,
          {
            page: state.page || 1,
            search: state.search,
            sortProperty: state.sortProperty,
            senderNameFilter: state.senderFilter.map((elem) => elem.value),
            emailSubjectFilter: state.subjectFilter.map((elem) => elem.value),
            recipientAddressFilter: state.mailboxFilter.map((elem) => elem.value),
            ipAddressFilter: state.ipsFilter.map((elem) => elem.value),
            attachmentsAndLinksFilter: state.attachmentsAndLinksFilter,
            notFoundFilter: state.notFoundFilter,
            isReadFilter: state.isReadFilter,
            includeCheckboxValues: false,
            reviewRequestedFilter: state.releaseRequestFilter,
          },
          true,
          state$.value.partnerOptions.selectedCompany?.value
        )
        .pipe(
          map((response) => {
            download(
              wrapCSVTextToBlob(response.response),
              xhrExtractFileName(response.xhr),
              response.responseType
            )
            return downloadCsvSuccess(response)
          }),
          catchError((error) => of(epicsErrorHandler(downloadCsvError, error))),
          takeUntil(action$.pipe(ofType(DATA_REQUEST_ASYNC.CANCEL)))
        )
    })
  )

const clusterUndoEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(CLUSTER_UNDO_ASYNC.INITIATE),
    mergeMap((action) => {
      const state = state$.value.resolutioncenter.affMailboxesDetail
      const body = getBodyForReclassify(state)

      return api.resolutionCenter.clusterUndo({ ...body, newState: action.payload.newState }).pipe(
        mergeMap((response) =>
          of(
            clusterUndoSuccess(response),
            addToast({ message: getSuccessToastMessage(response.response, true) })
          )
        ),
        catchError((error) =>
          of(
            epicsErrorHandler(clusterUndoError, error),
            addToast({ message: error.response?.message, type: 'error' })
          )
        ),
        takeUntil(action$.pipe(ofType(CLUSTER_UNDO_ASYNC.CANCEL)))
      )
    })
  )

const revertBackToOriginEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(REVERT_BACK_TO_ORIGIN_ASYNC.INITIATE),
    mergeMap(() => {
      const state = state$.value.resolutioncenter.affMailboxesDetail
      const body = getBodyForReclassify(state)
      return api.resolutionCenter.revertBackToOrigin(body).pipe(
        mergeMap((response) =>
          of(
            revertBackToOriginSuccess(response),
            addToast({
              message: getSuccessToastMessage(response.response, false),
            })
          )
        ),
        catchError((error) =>
          of(
            epicsErrorHandler(revertBackToOriginError, error),
            addToast({ message: error.response?.message, type: 'error' })
          )
        ),
        takeUntil(action$.pipe(ofType(REVERT_BACK_TO_ORIGIN_ASYNC.CANCEL)))
      )
    })
  )

const stopRemediationEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(STOP_REMEDIATION_ASYNC.INITIATE),
    mergeMap((action) =>
      api.resolutionCenter
        .stopRemediation(
          { report: action.payload.reportId },
          state$.value.partnerOptions.selectedCompany?.value
        )
        .pipe(
          map((response) => stopRemediationSuccess(response)),
          catchError((error) => of(epicsErrorHandler(stopRemediationError(), error)))
        )
    )
  )

const downloadEmailEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(DOWNLOAD_EMAIL_ASYNC.INITIATE),
    mergeMap((action) =>
      api.resolutionCenter.downloadIndividualEmail(action.payload.mitigationId).pipe(
        map((response) => {
          download(response, `${action.payload.mitigationId}`, response?.responseType)
          return downloadOriginalEmailSuccess()
        }),
        catchError((error) => {
          let parsedError = { message: 'Email cannot be downloaded' }
          if (error && error.response) {
            error.response.text().then((errorData) => {
              parsedError = JSON.parse(errorData)
            })
          }
          return of(
            epicsErrorHandler(
              () => downloadEmailError(error, action.payload.mitigationId),
              error,
              [401, 403, 404, 408]
            ),
            addToast({
              message: parsedError.message,
              type: 'error',
              id: 'Download_Email_Message_Error',
            })
          )
        })
      )
    )
  )

export default combineEpics(
  fetchDataEpic,
  clusterUndoEpic,
  downloadCsvEpic,
  stopRemediationEpic,
  revertBackToOriginEpic,
  downloadEmailEpic
)
